import React, { useMemo } from "react";

import { ReactComponent as ChromeIcon } from "@common/assets/images/billing/chrome-icon.svg";
import { ReactComponent as CodeIcon } from "@common/assets/images/billing/code-icon.svg";
import { ReactComponent as TeamMembersIcon } from "@common/assets/images/billing/team-members.svg";
import { ReactComponent as UniqueDevicesIcon } from "@common/assets/images/billing/unique-devices.svg";
import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import { HelperText } from "@common/components/HelperText/HelperText";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getBillableApps } from "@common/util/appsUtil";
import identifiers from "@common/util/identifiers.json";
import { quantity } from "@common/util/strings";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath } from "react-router-dom";

import * as Styled from "./CustomPricingSubscriptionCard.styles";

export function CustomPricingSubscriptionCard() {
  const { apps, wsTeamMembers } = useAppContext();
  const { selectedWorkspace } = useAppSelectionContext();

  const activeApps = useMemo(() => getBillableApps(apps), [apps]);

  const teamMembersNum: number = wsTeamMembers?.length ?? 0;
  const activeChromeExtensionAppsNum: number = activeApps.filter((a) => a.is_extension)?.length ?? 0;
  const activeSdkEmbeddedAppsNum: number = activeApps.filter((a) => !a.is_extension)?.length ?? 0;
  const uniqueDevicesNum: number = selectedWorkspace?.unique_devices ?? 0;

  return (
    <AdministrationCard
      title="Your subscription"
      testId={identifiers["subscription.header.yourSubscription"]}
    >
      <HelperText marginBottom={24}>
        <Paragraph>
          {"Read all about Shake plans, startup discounts and add-ons on "}
          <a
            href="https://www.shakebugs.com/pricing/"
            target="_blank"
            rel="noreferrer"
          >
            {"the pricing page"}
          </a>
          {"."}
        </Paragraph>
      </HelperText>

      <Paragraph
        fontWeight="bold"
        color={vars.colors.grey30}
        marginBottom={16}
      >
        {"Plan"}
      </Paragraph>

      <Flex
        $alignItems="flex-start"
        $gap={0.4}
        style={{ paddingBottom: "0.9rem" }}
      >
        <TeamMembersIcon />
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${teamMembersNum} `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_MEMBERS, {
              workspaceSlug: selectedWorkspace?.slug ?? null,
            })}
          >
            {`${quantity(teamMembersNum, "team member", "team members")}`}
          </Styled.LinkText>
          {` in this workspace`}
        </Styled.HeadingSubtitle>
      </Flex>

      <Flex
        $alignItems="flex-start"
        $gap={0.4}
        style={{ paddingBottom: "0.9rem" }}
      >
        <CodeIcon />
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${activeSdkEmbeddedAppsNum} active `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL, {
              workspaceSlug: selectedWorkspace?.slug ?? null,
            })}
          >
            {`${quantity(activeSdkEmbeddedAppsNum, "app", "apps")}`}
          </Styled.LinkText>
          {` with the SDK - to lower this, deactivate some of them`}
        </Styled.HeadingSubtitle>
      </Flex>

      <Flex
        $alignItems="center"
        $gap={0.4}
      >
        <ChromeIcon />
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${activeChromeExtensionAppsNum} active Chrome extension ${quantity(
            activeChromeExtensionAppsNum,
            "app",
            "apps",
          )}`}
        </Styled.HeadingSubtitle>
        <Styled.FreeAndUnlimitedNote>{"Free & unlimited"}</Styled.FreeAndUnlimitedNote>
      </Flex>

      <Paragraph
        fontWeight="bold"
        color={vars.colors.grey30}
        marginBottom={16}
        style={{ marginTop: "2.4rem" }}
      >
        {"Add - on"}
      </Paragraph>

      <Flex
        $alignItems="center"
        $gap={0.4}
      >
        <UniqueDevicesIcon />
        <Styled.HeadingSubtitle fontWeight={500}>
          {`${uniqueDevicesNum} SDK initializations in active apps - to lower this, deactivate some of the `}
          <Styled.LinkText
            to={generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL, {
              workspaceSlug: selectedWorkspace?.slug ?? "",
            })}
          >
            {"apps"}
          </Styled.LinkText>
        </Styled.HeadingSubtitle>
      </Flex>

      <HelperText marginTop={24}>
        <Paragraph>
          {
            "Your workspace pays Shake manually once a year via an invoice we send you. There is no credit card on file and the system does not have an invoice history. For any details, contact us at "
          }
          <a
            href="mailto:friends@shakebugs.com"
            target="_blank"
            rel="noreferrer"
          >
            {"friends@shakebugs.com"}
          </a>
          {"."}
        </Paragraph>
      </HelperText>
    </AdministrationCard>
  );
}
